/***
 *
 *   PAGINATE
 *   Pagination control to split results into multiple pages.
 *   Returns a new offset.
 *
 *   Ported from ui-reference/src/components/ui/table-pagination.tsx: the range
 *   sits on the left of a top-bordered footer row, the rows-per-page select
 *   and the prev/next controls on the right as secondary buttons that disable
 *   at the ends.
 *
 *   The current page is derived from `offset` rather than held locally. Kept
 *   locally it drifted out of step with the parent the moment a filter reset
 *   the offset: the reader was back on page one while the buttons still
 *   thought they were on page four.
 *
 *   PROPS
 *   total: the total number of results
 *   limit: the number of results per page
 *   offset: the current position
 *   onChange: receives the new offset
 *   pageSize / pageSizeOptions / onPageSizeChange: optional rows-per-page select
 *
 **********/

import { Button, ClassHelper } from 'components/lib';
import Style from './paginate.tailwind.js';
import { useTranslate } from 'app/translations.js';

export function Paginate(props) {
    const { _t } = useTranslate();

    const total = parseInt(props.total, 10) || 0;
    const limit = parseInt(props.limit, 10) || 10;
    const offset = parseInt(props.offset, 10) || 0;

    const totalPages = Math.max(Math.ceil(total / limit), 1);
    const page = Math.floor(offset / limit);

    const canPrev = page > 0;
    const canNext = page < totalPages - 1;

    const start = total === 0 ? 0 : offset + 1;
    const end = Math.min(offset + limit, total);

    const paginateStyle = ClassHelper(Style, {
        paginate: true,
        className: props.className,
    });

    return (
        <section className={paginateStyle}>
            <p className={Style.label}>
                {total === 0
                    ? _t('result.none')
                    : _t('table.showing', { start: start, end: end, total: total })}
            </p>

            <div className={Style.controls}>
                {props.onPageSizeChange && (
                    <label className={Style.sizeLabel}>
                        <span>{_t('table.rowsPerPage')}</span>
                        <select
                            className={Style.select}
                            value={props.pageSize || limit}
                            aria-label={_t('table.rowsPerPage')}
                            onChange={(e) => props.onPageSizeChange(Number(e.target.value))}>
                            {(props.pageSizeOptions || [10, 25, 50]).map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                    </label>
                )}

                <span className={Style.counter}>
                    {_t('table.page', { page: Math.min(page + 1, totalPages), pages: totalPages })}
                </span>

                <div className={Style.buttons}>
                    <Button
                        icon="chevron-left"
                        variant="secondary"
                        size="sm"
                        className={Style.button}
                        disabled={!canPrev}
                        title={_t('table.previousPage')}
                        action={() => canPrev && props.onChange((page - 1) * limit)}
                    />
                    <Button
                        icon="chevron-right"
                        variant="secondary"
                        size="sm"
                        className={Style.button}
                        disabled={!canNext}
                        title={_t('table.nextPage')}
                        action={() => canNext && props.onChange((page + 1) * limit)}
                    />
                </div>
            </div>
        </section>
    );
}
